import {useContext, useEffect} from 'react';

import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import {Box, Divider, Typography} from '@mui/material';

import {AnalyticsSection} from './components/AnalyticsSection';
import {Loading} from '../../../components/Loading/Loading';
import {LanguageContext} from '../../../contexts/LanguageContext/LanguageContext';
import {SnackBarContext} from '../../../contexts/SnackBarContext/SnackBarContext';
import {useApiRead} from '../../../hooks/useApiRead';
import {GetRoutes} from '../../../lib/api/GET/GetRoutes';
import {getCampaignStatusTranslation} from '../../../types/Campaign';
import {CampaignAnalyticsResponse, SectionCount} from '../../../types/CampaignAnalytics';
import {formatNumber} from '../../../utils/numberFormattingUtils/numberFormattingUtils';

export const CampaignsAnalyticsDashboard = () => {
  const {t} = useTranslation();
  const currentLanguage = useContext(LanguageContext);

  const urlParams = useParams();
  const campaignId = urlParams.id || '';
  const {dispatchMessage} = useContext(SnackBarContext);

  const {data, isLoading, error} = useApiRead<CampaignAnalyticsResponse>({
    path: GetRoutes.getMarketingCampaignAnalytics(campaignId),
    options: {enabled: !!campaignId},
  });

  const sortByRate = (sections: Record<string, SectionCount>) => {
    return Object.values(sections).sort((a, b) => {
      const aPercent = parseFloat(a.rate.replace('%', ''));
      const bPercent = parseFloat(b.rate.replace('%', ''));
      return bPercent - aPercent;
    });
  };

  useEffect(() => {
    if (error) {
      dispatchMessage(t('shared.fetchingData.error'), 'error');
    }
  }, [error, dispatchMessage, t]);

  if (isLoading) {
    return <Loading backDrop />;
  }

  return (
    <>
      <Typography variant='h1' gutterBottom>
        {t('campaignAnalytics.header')}{' '}
      </Typography>
      <Box width='100%' padding='30px' paddingBottom='52px' sx={{backgroundColor: '#fff', borderRadius: '16px'}} mt={4}>
        <Typography
          variant='h2'
          component={'span'}
          sx={{
            fontWeight: 'bold',
            textDecoration: 'underline',
          }}
        >
          {data?.name}
        </Typography>
        <Divider sx={{marginY: 3}} />

        {/* 
      NOTE: Commented out for now 
      This value is not calculated yet in the backend
       */}
        {/* <AnalyticsSection
        title={t('campaignAnalytics.sections.performance')}
        statuses={[{label: data.evaluation, selected: true}]}
      /> */}

        <AnalyticsSection
          title={t('campaignAnalytics.sections.distribution')}
          gridRows={6}
          metrics={[
            {
              label: t('campaignsPage.tableHeaders.status'),
              value: data?.status ? getCampaignStatusTranslation(data.status, currentLanguage) : '-',
            },
            {
              label: t('campaignAnalytics.labels.impressions'),
              value: formatNumber(data?.campaignKpis?.impressions || 0, currentLanguage),
            },
          ]}
        />

        <AnalyticsSection
          title={t('campaignAnalytics.sections.advertisement')}
          metrics={[
            {
              label: t('campaignAnalytics.labels.visitors'),
              value: formatNumber(data?.campaignKpis?.uniqueVisitors || 0, currentLanguage),
            },
            {
              label: t('campaignAnalytics.labels.testTaken'),
              value: formatNumber(data?.campaignKpis?.testResponseCount || 0, currentLanguage),
            },
            {
              label: t('campaignAnalytics.labels.requirementsMet'),
              value: formatNumber(data?.campaignKpis?.topResults || 0, currentLanguage),
            },
          ]}
        />

        <AnalyticsSection
          title={t('campaignAnalytics.sections.candidates')}
          metrics={[
            {
              label: t('campaignAnalytics.labels.applied'),
              value: formatNumber(data?.campaignKpis?.finishedResults || 0, currentLanguage),
            },
            {
              label: t('campaignAnalytics.labels.pending'),
              value: formatNumber(data?.campaignKpis?.pending || 0, currentLanguage),
            },
            {
              label: t('campaignAnalytics.labels.matched'),
              value: formatNumber(data?.campaignKpis?.submitted || 0, currentLanguage),
            },
          ]}
          infoTooltip={t('campaignAnalytics.infoTooltips.candidates') || ''}
        />

        <AnalyticsSection
          title={t('campaignAnalytics.sections.matches')}
          metrics={[
            {
              label: t('campaignAnalytics.labels.accepted'),
              value: data?.matches?.accepted || 0,
            },
            {
              label: t('campaignAnalytics.labels.contacted'),
              value: data?.matches?.contacted || 0,
            },
            {
              label: t('campaignAnalytics.labels.interviewed'),
              value: data?.matches?.interviewed || 0,
            },
            {
              label: t('campaignAnalytics.labels.hired'),
              value: data?.matches?.hired || 0,
            },
            {
              label: t('campaignAnalytics.labels.rejected'),
              value: data?.matches?.rejected || 0,
            },
          ]}
          gridRows={2}
          infoTooltip={t('campaignAnalytics.infoTooltips.matches') || ''}
        />

        <AnalyticsSection
          title={t('campaignAnalytics.sections.marketFeedback')}
          breakdownItems={sortByRate(data?.campaignKpis?.sectionsCount || {}).map((section) => ({
            label: section.name[currentLanguage],
            value: section.rate,
          }))}
        />

        <AnalyticsSection
          title={t('campaignAnalytics.sections.marketHurdles')}
          breakdownItems={sortByRate(data?.campaignKpis?.sectionsLeaves || {}).map((section) => ({
            label: section.name[currentLanguage],
            value: section.rate,
          }))}
        />
      </Box>
    </>
  );
};
